import { inspectsMutate } from "@/api/inspect/useInspectQuery";
import { COMMON_TEXT } from "@/constants/text";
import { customNotification } from "@/utils/notificationShow";
import {
  InspectionsGet200ResponseRowsInner,
  InspectionsInspectionIdRecordsPutRequest,
  ProductionPlansGet200ResponseRowsInnerWorksInnerInspectionsInnerSpecificationDataCriteriaInner,
  SpcApiInspectionsInspectionIdDeleteRequest,
  SpcApiInspectionsInspectionIdRecordsPutRequest,
  SpcApiInspectionsInspectionIdSubmitPostRequest,
  SpcApiInspectionsPostRequest,
} from "@sizlcorp/sizl-api-document/dist/models";
import { useMutation } from "@tanstack/react-query";
import { useState } from "react";

export type onSubmitType = {
  // 직접 판단에 필요한 최소 정보만 전달받아 handler 내부에서 분기
  inspectionId: number; // 현재(서브)검사 ID
  record: any[][]; // 현재 탭의 입력 결과 2차원 배열
  baseRecord?: any[][]; // 직전 기준(없으면 신규)
  description?: string;
  hasPrevious: boolean; // 이전 검사 진행여부(있으면 update, 없으면 submit)
  criteriaTypes?: string[]; // description 판정 계산에 사용(선택)
};

export const useInspectHandler = () => {
  // 서버 응답 구조에 subInspections 등이 포함될 수 있어 any로 유지
  const [specificationPostData, setSpecificationPostData] = useState<
    InspectionsGet200ResponseRowsInner | undefined
  >(undefined);

  const { mutate: createMutate, mutateAsync: createMutateAsync } = useMutation(
    (params: SpcApiInspectionsPostRequest) =>
      inspectsMutate.create(params).mutationFn(undefined),
    {
      onSuccess: (res) => {
        customNotification.success({
          message: COMMON_TEXT.NOTIFICATION.INSPECT_SUCCESS,
        });
        setSpecificationPostData(res.data);
      },
    }
  );

  const { mutate: updateMutate, isLoading: updateLoading } = useMutation(
    (params: SpcApiInspectionsInspectionIdRecordsPutRequest) =>
      inspectsMutate.update(params).mutationFn(undefined),
    {
      onSuccess: () => {
        customNotification.success({
          message: COMMON_TEXT.NOTIFICATION.INSPECT_SUCCESS,
        });
      },
    }
  );

  const {
    mutate: deleteMutate,
    mutateAsync: deleteMutateAsync,
    isLoading: deleteLoading,
  } = useMutation(
    (params: SpcApiInspectionsInspectionIdDeleteRequest) =>
      inspectsMutate.delete(params).mutationFn(undefined),
    {
      onSuccess: () => {
        customNotification.success({
          message: COMMON_TEXT.NOTIFICATION.INSPECT_SUCCESS,
        });
      },
    }
  );

  const { mutate: submitMutate, isLoading: submitLoading } = useMutation(
    (params: SpcApiInspectionsInspectionIdSubmitPostRequest) =>
      inspectsMutate.submit(params).mutationFn(undefined)
  );

  const onSubmit = ({
    inspectionId,
    record,
    baseRecord,
    description,
    hasPrevious,
    criteriaTypes,
  }: onSubmitType) => {
    if (!hasPrevious) {
      // 신규 제출
      submitMutate({
        inspectionId,
        inspectionsInspectionIdSubmitPostRequest: {
          record,
          description,
        },
      });
      return;
    }

    // 진행 이력 존재 → 변경분만 업데이트
    const updates = (record || []).flatMap(
      (criteriaArray, criteriaIndex: number) =>
        (criteriaArray || []).flatMap((sample, sampleIndex: number) => {
          const prev = baseRecord?.[criteriaIndex]?.[sampleIndex];
          const prevValue = prev ? prev.value ?? null : null;
          const nextValue = sample?.value ?? null;
          const type = criteriaTypes?.[criteriaIndex];
          // description은 체크박스 값만 반영. 그 외 타입은 기존 isPassed 유지
          const prevIsPassed = prev ? (prev as any).isPassed : undefined;
          const nextIsPassed =
            type === "description" ? sample?.isPassed : sample?.isPassed;
          if (
            prevValue !== nextValue ||
            (type === "description" && prevIsPassed !== nextIsPassed)
          ) {
            const payload: any = {
              criteriaIndex,
              sampleIndex,
              value: nextValue,
              inspectionId,
            };
            // isPassed는 정의된 경우에만 포함
            if (nextIsPassed !== undefined) payload.isPassed = nextIsPassed;
            return [payload];
          }
          return [];
        })
    );
    if (updates.length > 0) {
      updateMutate({
        inspectionId,
        inspectionsInspectionIdRecordsPutRequest: {
          updates,
        } as InspectionsInspectionIdRecordsPutRequest,
      });
    }
  };

  return {
    onSubmit,
    createMutate,
    createMutateAsync,
    deleteMutate,
    deleteMutateAsync,
    specificationPostData,
    resetSpecificationPostData: () => setSpecificationPostData(undefined),
  };
};
